// Interfaz DOM de la captura (Shadow DOM, sin frameworks). Todo el texto sale de los diccionarios.
// Accesible: región `aria-live`, foco en el título de cada pantalla, botones y disparador por
// teclado (Espacio/Intro), barra de progreso con `role=progressbar` y voz opcional.
import type { ChallengeDisplay } from '../ChallengeRunner.js';
import type { MessageKey, Translate } from '../i18n/index.js';
import { VIEWS, type CaptureUI, type Screen, type UIAction } from '../screens.js';
import type { CaptureLocale, CaptureStep } from '../types.js';
import { STYLES } from './styles.js';

export interface DomCaptureUIOptions {
  voice: boolean;
  locale: CaptureLocale;
  rulerUrl?: string;
}

const STEPS: readonly CaptureStep[] = [
  'permission',
  'label',
  'challenge',
  'views',
  'ruler',
  'declarations',
  'summary',
];

type Attrs = Record<string, string | boolean | undefined>;

/** Crea un elemento con atributos e hijos (texto o nodos). */
function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: (Node | string | null | undefined | false)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    el.setAttribute(k, v === true ? '' : v);
  }
  for (const c of children) if (c) el.append(c);
  return el;
}

/**
 * SVG del servidor (QR) saneado: solo si empieza por `<svg`; sin scripts, `foreignObject`,
 * enlaces, imágenes externas, estilos ni atributos `on*`.
 */
export function sanitizeSvg(svg: string): SVGElement | null {
  const s = svg.trim();
  if (!s.startsWith('<svg') || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(s, 'image/svg+xml');
  const root = doc.documentElement;
  if (root.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length)
    return null;
  root
    .querySelectorAll('script,foreignObject,iframe,object,embed,use,a,image,style,animate,set')
    .forEach((n) => n.remove());
  for (const el of [root, ...Array.from(root.querySelectorAll('*'))])
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on') || name === 'href' || name.endsWith(':href') || name === 'style')
        el.removeAttribute(attr.name);
    }
  return document.importNode(root, true) as unknown as SVGElement;
}

/** ¿URL http(s) segura para un enlace? */
function safeUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url, globalThis.location?.href);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : undefined;
  } catch {
    return undefined;
  }
}

/** Texto legible sobre un color de fondo (#rrggbb) atenuado por `intensity`. */
function scaled(color: string, intensity: number): { bg: string; fg: string } {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  const [r, g, b] = m ? [1, 2, 3].map((i) => Number.parseInt(m[i]!, 16)) : [0, 0, 0];
  const k = Math.max(0, Math.min(1, intensity));
  const rr = Math.round(r! * k);
  const gg = Math.round(g! * k);
  const bb = Math.round(b! * k);
  const luma = 0.299 * rr + 0.587 * gg + 0.114 * bb;
  return { bg: `rgb(${rr},${gg},${bb})`, fg: luma > 140 ? '#0B0D10' : '#FAFAF7' };
}

/** Interfaz de la captura montada en un Shadow DOM. */
export class DomCaptureUI implements CaptureUI {
  private readonly root: ShadowRoot;
  private readonly app: HTMLDivElement;
  private readonly stepLabel: HTMLSpanElement;
  private readonly bar: HTMLDivElement;
  private readonly fillBar: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly video: HTMLVideoElement;
  private readonly panel: HTMLElement;
  private readonly overlay: HTMLDivElement;
  private readonly overlayHint: HTMLDivElement;
  private readonly orbit: HTMLSpanElement;
  private resolver: ((a: UIAction) => void) | null = null;
  private screenKey = '';
  private form: HTMLFormElement | null = null;
  private readonly onKey = (ev: KeyboardEvent) => this.key(ev);

  constructor(
    private readonly host: HTMLElement,
    private readonly t: Translate,
    private readonly opts: DomCaptureUIOptions,
  ) {
    this.root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    this.root.replaceChildren();
    const style = h('style');
    style.textContent = STYLES;
    this.stepLabel = h('span', { class: 'steplabel' });
    this.fillBar = h('div', { class: 'fill' });
    this.bar = h(
      'div',
      { class: 'bar', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100' },
      this.fillBar,
    );
    this.video = h('video', { playsinline: true, muted: true, autoplay: true });
    this.video.muted = true;
    this.stage = h(
      'div',
      { class: 'stage', hidden: true },
      this.video,
      h('div', { class: 'frame' }),
    );
    this.panel = h('section', { class: 'panel', 'aria-live': 'polite' });
    this.overlayHint = h('div', { class: 'hint' });
    this.orbit = h('span');
    this.overlay = h(
      'div',
      { class: 'overlay', hidden: true, role: 'status', 'aria-live': 'assertive' },
      h(
        'div',
        {},
        this.overlayHint,
        h('span', { class: 'orbit', 'aria-hidden': 'true' }, this.orbit),
      ),
    );
    this.app = h(
      'div',
      { class: 'kyp', lang: opts.locale },
      h(
        'header',
        {},
        h('span', { class: 'seal', 'aria-hidden': 'true' }),
        h('span', { class: 'brand' }, t('app.title')),
        this.stepLabel,
        this.bar,
      ),
      this.stage,
      this.panel,
      this.overlay,
    );
    this.root.append(style, this.app);
    // En el host: el teclado funciona con el foco en cualquier parte del componente.
    this.host.addEventListener('keydown', this.onKey);
  }

  action(): Promise<UIAction> {
    return new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  preview(stream: MediaStream | null): HTMLVideoElement | null {
    this.video.srcObject = stream;
    if (stream) void this.video.play?.()?.catch?.(() => undefined);
    return this.video;
  }

  fill(name: string, value: string): void {
    const input = this.form?.elements.namedItem(name);
    if (input instanceof HTMLInputElement && !input.value) input.value = value;
  }

  challengeDisplay(): ChallengeDisplay {
    return {
      begin: () => {
        this.overlay.hidden = false;
        this.overlayHint.textContent = this.t('challenge.running');
        this.speak(this.t('challenge.running'));
      },
      show: (color, intensity) => {
        const { bg, fg } = scaled(color, intensity);
        this.overlay.style.background = bg;
        this.overlay.style.color = fg;
      },
      motion: (progress) => {
        this.orbit.style.width = `${Math.round(progress * 100)}%`;
      },
      end: () => {
        this.overlay.hidden = true;
        this.overlay.style.background = '';
      },
    };
  }

  destroy(): void {
    this.host.removeEventListener('keydown', this.onKey);
    this.video.srcObject = null;
    this.root.replaceChildren();
    this.resolver = null;
    globalThis.speechSynthesis?.cancel?.();
  }

  render(screen: Screen): void {
    const key = screen.kind + ('view' in screen ? `:${screen.view}` : '');
    const same = key === this.screenKey;
    const kept = same ? this.formValues() : {};
    this.screenKey = key;
    this.stage.hidden = !['label', 'challenge', 'view'].includes(screen.kind);
    this.progress(screen);

    const title = h('h2', { tabindex: '-1' });
    const body = h('p', { class: 'body' });
    const msg = h('div', { class: 'msg', role: 'status' });
    const actions = h('div', { class: 'actions' });
    this.form = null;
    const content: Node[] = [title, body, msg];
    const t = this.t;

    switch (screen.kind) {
      case 'loading':
        title.textContent = t('app.loading');
        break;
      case 'permission':
        title.textContent = t('permission.title');
        body.textContent = t('permission.body');
        if (screen.checking) msg.textContent = t('permission.checking');
        actions.append(this.button('permission.button', 'primary', screen.checking));
        break;
      case 'label': {
        title.textContent = t('label.title');
        body.textContent = t('label.body');
        if (screen.detected) msg.textContent = t('label.detected', { code: screen.detected });
        this.warning(msg, screen.warning);
        this.form = this.fields([['tracking', 'label.tracking', 'text', screen.tracking]]);
        content.push(this.form);
        if (screen.warning) actions.append(...this.qualityButtons());
        else actions.append(this.button('label.shoot', 'primary', screen.busy, 'shutter.label'));
        break;
      }
      case 'challenge':
        title.textContent = t('challenge.title');
        body.textContent =
          screen.phase === 'running' ? t('challenge.running') : t('challenge.body');
        if (screen.phase === 'done') msg.textContent = t('challenge.done');
        if (screen.phase === 'intro') actions.append(this.button('challenge.start', 'primary'));
        break;
      case 'view': {
        const name = t(`views.${screen.view}` as MessageKey);
        title.textContent = t('views.title', { n: screen.n, total: screen.total, name });
        body.textContent = t(`views.hint.${screen.view}` as MessageKey);
        if (screen.busy) msg.textContent = t('views.shooting');
        this.warning(msg, screen.warning);
        if (screen.warning) actions.append(...this.qualityButtons());
        else actions.append(this.button('views.shoot', 'primary', screen.busy, 'shutter.label'));
        break;
      }
      case 'ruler': {
        title.textContent = t('ruler.title');
        body.textContent = t('ruler.body');
        const url = safeUrl(screen.rulerUrl ?? this.opts.rulerUrl);
        if (url)
          content.push(
            h('p', {}, h('a', { href: url, target: '_blank', rel: 'noopener' }, t('ruler.link'))),
          );
        this.form = this.fields([
          ['l', 'ruler.length', 'number'],
          ['w', 'ruler.width', 'number'],
          ['h', 'ruler.height', 'number'],
        ]);
        const unit = h('select', { name: 'unit' });
        for (const u of ['cm', 'mm', 'in']) unit.append(h('option', { value: u }, u));
        this.form.append(
          h('label', {}, t('ruler.unit'), unit),
          h('p', { class: 'body' }, t('ruler.estimated')),
        );
        content.push(this.form);
        actions.append(this.button('app.next', 'primary'), this.button('app.skip', 'skip'));
        break;
      }
      case 'declarations':
        title.textContent = t('declarations.title');
        body.textContent = t('declarations.body');
        this.form = this.fields([
          ['tracking', 'declarations.tracking', 'text', screen.tracking],
          ['weight', 'declarations.weight', 'number'],
          ['content', 'declarations.content', 'text'],
        ]);
        content.push(this.form);
        actions.append(this.button('app.next', 'primary'), this.button('app.skip', 'skip'));
        break;
      case 'summary': {
        title.textContent = screen.result ? t('result.title') : t('summary.title');
        body.textContent = screen.result ? '' : t('summary.body');
        content.push(
          h('p', {}, t('summary.shots', { n: screen.shots })),
          h('p', {}, t('summary.video', { n: screen.chunks })),
        );
        if (screen.upload && screen.phase === 'uploading')
          msg.textContent = t('summary.uploading', {
            done: screen.upload.done,
            total: screen.upload.total,
          });
        content.push(this.stages(screen.result));
        if (screen.result) content.push(...this.result(screen.result));
        if (screen.phase === 'ready') actions.append(this.button('summary.submit', 'primary'));
        break;
      }
      case 'rejected':
        title.textContent = t('rejected.title');
        body.textContent = t(`rejected.${screen.reason}` as MessageKey);
        break;
      case 'handoff': {
        title.textContent = t('handoff.title');
        body.textContent = t('handoff.body');
        const qr = sanitizeSvg(screen.handoff.qr_svg);
        if (qr)
          content.push(
            h('div', { class: 'qr', role: 'img', 'aria-label': t('handoff.title') }, qr),
          );
        content.push(h('p', { class: 'code' }, t('handoff.code', { code: screen.handoff.code })));
        const url = safeUrl(screen.handoff.url);
        if (url) content.push(h('p', {}, h('a', { href: url }, t('handoff.link'))));
        msg.textContent =
          screen.status === 'expired'
            ? t('handoff.expired')
            : screen.status === 'waiting'
              ? t('handoff.waiting')
              : screen.step
                ? t('step.progress', {
                    n: STEPS.indexOf(screen.step) + 1,
                    total: STEPS.length,
                    name: t(`step.${screen.step}` as MessageKey),
                  })
                : t('handoff.opened');
        if (screen.result) content.push(this.stages(screen.result), ...this.result(screen.result));
        break;
      }
      case 'error':
        title.textContent = t('app.error');
        break;
    }
    content.push(actions);
    this.panel.replaceChildren(...content);
    if (same) this.restore(kept);
    else {
      title.focus({ preventScroll: true });
      this.speak([title.textContent, body.textContent].filter(Boolean).join('. '));
    }
  }

  // ── piezas ──

  private progress(screen: Screen): void {
    const step: CaptureStep | undefined =
      screen.kind === 'view'
        ? 'views'
        : screen.kind === 'handoff'
          ? screen.step
          : (STEPS as readonly string[]).includes(screen.kind)
            ? (screen.kind as CaptureStep)
            : undefined;
    if (!step) {
      this.stepLabel.replaceChildren();
      return;
    }
    const n = STEPS.indexOf(step) + 1;
    const viewShare =
      screen.kind === 'view' ? (VIEWS.indexOf(screen.view) + 1) / (VIEWS.length + 1) : 0;
    const done =
      screen.kind === 'summary' && screen.result ? 100 : ((n - 1 + viewShare) / STEPS.length) * 100;
    const pct = screen.kind === 'handoff' && screen.percent !== undefined ? screen.percent : done;
    this.stepLabel.textContent = this.t('step.progress', {
      n,
      total: STEPS.length,
      name: this.t(`step.${step}` as MessageKey),
    });
    this.fillBar.style.width = `${Math.round(pct)}%`;
    this.bar.setAttribute('aria-valuenow', String(Math.round(pct)));
  }

  private button(
    key: MessageKey,
    kind: 'primary' | 'skip' | 'retry',
    disabled = false,
    ariaKey?: MessageKey,
  ): HTMLButtonElement {
    const b = h(
      'button',
      {
        type: 'button',
        class: kind === 'primary' ? 'primary' : undefined,
        disabled,
        'aria-label': ariaKey ? this.t(ariaKey) : undefined,
      },
      this.t(key),
    );
    b.addEventListener('click', () => this.fire(kind));
    return b;
  }

  private qualityButtons(): HTMLButtonElement[] {
    return [this.button('quality.keep', 'primary'), this.button('quality.retry', 'retry')];
  }

  private warning(msg: HTMLElement, warning?: 'dark' | 'blurry'): void {
    if (!warning) return;
    msg.textContent = this.t(warning === 'dark' ? 'quality.dark' : 'quality.blurry');
    msg.classList.add('warn');
    this.speak(msg.textContent);
  }

  private fields(defs: [string, MessageKey, 'text' | 'number', string?][]): HTMLFormElement {
    const form = h('form', { novalidate: true });
    for (const [name, label, type, value] of defs) {
      const input = h('input', {
        name,
        type,
        inputmode: type === 'number' ? 'decimal' : undefined,
        min: type === 'number' ? '0' : undefined,
        step: type === 'number' ? 'any' : undefined,
        maxlength: type === 'text' ? '200' : undefined,
        autocomplete: 'off',
      });
      if (value) input.value = value;
      form.append(h('label', {}, this.t(label), input));
    }
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      this.fire('primary');
    });
    return form;
  }

  private stages(result?: { stages: { name: string }[] }): HTMLElement {
    const on = new Set(result?.stages.map((s) => s.name) ?? []);
    const list = h('ol', { class: 'stages' });
    for (const s of ['received', 'signed', 'sealed'] as const)
      list.append(
        h(
          'li',
          { class: on.has(s) ? 'on' : undefined },
          this.t(`summary.stage.${s}` as MessageKey),
        ),
      );
    return list;
  }

  private result(r: {
    confidence_level: string;
    score: number;
    peid?: string;
    verify_url?: string;
    findings: unknown[];
  }): Node[] {
    const t = this.t;
    const out: Node[] = [
      h(
        'p',
        {},
        t('result.level', { level: t(`result.level.${r.confidence_level}` as MessageKey) }),
      ),
      h('p', {}, t('result.score', { score: r.score })),
      h('p', {}, t('result.findings', { n: r.findings.length })),
    ];
    if (r.peid) out.push(h('p', {}, t('result.peid', { peid: r.peid })));
    const url = safeUrl(r.verify_url);
    if (url)
      out.push(
        h('p', {}, h('a', { href: url, target: '_blank', rel: 'noopener' }, t('result.verify'))),
      );
    return out;
  }

  private formValues(): Record<string, string> {
    const out: Record<string, string> = {};
    if (!this.form) return out;
    for (const el of Array.from(this.form.elements))
      if ((el instanceof HTMLInputElement || el instanceof HTMLSelectElement) && el.name)
        out[el.name] = el.value;
    return out;
  }

  private restore(values: Record<string, string>): void {
    if (!this.form) return;
    for (const [name, value] of Object.entries(values)) {
      const el = this.form.elements.namedItem(name);
      if ((el instanceof HTMLInputElement || el instanceof HTMLSelectElement) && value)
        el.value = value;
    }
  }

  private fire(kind: 'primary' | 'skip' | 'retry'): void {
    const resolve = this.resolver;
    if (!resolve) return;
    this.resolver = null;
    if (kind === 'primary') resolve({ type: 'primary', values: this.formValues() });
    else resolve({ type: kind });
  }

  /** Espacio o Intro fuera de campos y botones = botón principal (disparador). */
  private key(ev: KeyboardEvent): void {
    if (ev.key !== ' ' && ev.key !== 'Enter') return;
    const target = ev.composedPath()[0] as HTMLElement | undefined;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'button' || tag === 'a' || tag === 'select' || tag === 'textarea') return;
    if (tag === 'input' && ev.key === ' ') return;
    const primary = this.panel.querySelector<HTMLButtonElement>('button.primary');
    if (!primary || primary.disabled) return;
    ev.preventDefault();
    this.fire('primary');
  }

  private speak(text: string | null): void {
    if (!this.opts.voice || !text) return;
    const synth = globalThis.speechSynthesis;
    const U = globalThis.SpeechSynthesisUtterance;
    if (!synth || !U) return;
    synth.cancel();
    const u = new U(text);
    u.lang = this.opts.locale === 'en' ? 'en-GB' : 'es-ES';
    synth.speak(u);
  }
}
